import { GridColDef } from "@mui/x-data-grid";
import EditTwoToneIcon from "@mui/icons-material/EditTwoTone";
import DeleteTwoToneIcon from "@mui/icons-material/DeleteTwoTone";
import { Box, IconButton, Radio, Tooltip, Typography } from "@mui/material";
import { ICompanyOffice } from "../../Interfaces";

const emDash = (value: any) =>
  value === null || value === undefined || `${value}`.trim() === "" ? "—" : `${value}`;

/**
 * The site grid (S3.3): the site name leads, then its sub code, the address, the region and the
 * points of contact stacked one per line. An empty sub code or address renders an em dash.
 *
 * Text columns flex so the row always fills the grid's width, whether it renders full-width in the
 * wizard or beside the client record's sidebar; only the fixed-content columns keep a set width.
 * The primary-office radio moves the primary across the list (S3.5), and Remove cascades (S3.9).
 */
export const NO_CLINIC_PICKED = "Not picked";

export const officeColumns = (
  updateOffice: (office: ICompanyOffice) => void,
  makePrimary: (office: ICompanyOffice) => void,
  removeOffice: (office: ICompanyOffice) => void,
  readOnly: boolean = false,
  /** In the wizard a site without its Imperans clinic is called out, not shown as an em dash (story 6373). */
  requireServicingClinic: boolean = false,
): GridColDef[] => {
  return [
    // Office/Site Name leads deliberately. S3.1 lists the columns as sub code first, and task 5726
    // raised the difference — but the name is what a reader scans for, and leading with a code means
    // reading the identifier before knowing whose it is. Confirmed as a request, 26 Aug 2026: the
    // grid stays this way and S3.1's column order is the part that wants amending.
    {
      field: "officeName",
      headerName: "Office/Site Name",
      editable: false,
      flex: 1.2,
      minWidth: 170,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ py: 0.5 }}>
          <Typography variant="body2">{emDash(params.row.officeName)}</Typography>
          {params.row.division && (
            <Typography variant="caption" color="text.secondary" display="block">
              {params.row.division}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: "subCode",
      headerName: "Sub Code",
      editable: false,
      width: 110,
      sortable: false,
      renderCell: (params) => <span>{emDash(params.row.subCode)}</span>,
    },
    {
      // Single-string site address (decision 13).
      field: "officeFullAddress",
      headerName: "Address",
      editable: false,
      flex: 1.6,
      minWidth: 200,
      sortable: false,
      renderCell: (params) => <span>{emDash(params.row.officeFullAddress)}</span>,
    },
    {
      field: "regionName",
      headerName: "Region",
      editable: false,
      flex: 0.8,
      minWidth: 110,
      sortable: false,
      renderCell: (params) => <span>{emDash(params.row.regionName)}</span>,
    },
    {
      // The Imperans clinic that services the site, picked from the paired Imperans's list (Epic
      // 6303, story 6373). Every site needs one before the wizard's step 3 reads complete.
      field: "imperansFranchiseOfficeName",
      headerName: "Servicing clinic",
      editable: false,
      flex: 1,
      minWidth: 140,
      sortable: false,
      renderCell: (params) =>
        params.row.imperansFranchiseOfficeID ? (
          <span>{emDash(params.row.imperansFranchiseOfficeName)}</span>
        ) : (
          <Typography
            variant="body2"
            color={requireServicingClinic ? "error" : "text.secondary"}
          >
            {requireServicingClinic ? NO_CLINIC_PICKED : "—"}
          </Typography>
        ),
    },
    {
      field: "pointsOfContact",
      headerName: "Points of contact",
      editable: false,
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => {
        const contacts = params.row.pointsOfContact || [];
        if (!contacts.length) return <span>—</span>;
        return (
          <Box sx={{ py: 0.5 }}>
            {contacts.map((contact) => (
              <Typography
                key={contact.companyContactID || contact.emailAddress}
                variant="caption"
                display="block"
              >
                {emDash(contact.fullName)}
                {contact.coversAllSites ? " (all sites)" : ""}
              </Typography>
            ))}
          </Box>
        );
      },
    },
    // No Employees column: S3.1 lists the grid as sub code, name with division beneath, address and
    // points of contact, and a per-site headcount was never one of them (task 5726). The value is
    // still captured on the site and still prints on the SOP — this only stops the grid showing a
    // column the spec does not have, which also buys width back for the four that matter.
    {
      field: "isPrimaryOffice",
      headerName: "Primary",
      editable: false,
      width: 90,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Tooltip title={readOnly ? "" : "Make this the primary office"}>
          <Radio
            size="small"
            checked={Boolean(params.row.isPrimaryOffice)}
            disabled={readOnly || Boolean(params.row.isPrimaryOffice)}
            onChange={() => makePrimary(params.row)}
          />
        </Tooltip>
      ),
    },
    {
      field: "edit",
      headerName: "",
      editable: false,
      width: 96,
      sortable: false,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <>
          <IconButton
            size="small"
            color="primary"
            disabled={readOnly}
            onClick={() => updateOffice(params.row)}
          >
            <EditTwoToneIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            disabled={readOnly}
            onClick={() => removeOffice(params.row)}
          >
            <DeleteTwoToneIcon fontSize="small" />
          </IconButton>
        </>
      ),
    },
  ];
};
